/**
 * PWA-4 — pure sync-queue logic. No DOM, no Firebase, no IndexedDB: storage,
 * executors and the clock are all injected, so every rule below is unit-tested
 * without a browser (see queue.test.ts) and Stage 2 can reuse it verbatim.
 */

import type {
  DrainResult,
  ExecutorMap,
  QueuedAction,
  SyncActionType,
  SyncQueueStorage,
} from './types';

/** Give up retrying after this many attempts and flag the action for the user. */
export const MAX_ATTEMPTS = 5;

/**
 * How long a finished producer's receipt is kept after nothing is waiting
 * on it. Long enough for a dependant enqueued late — a photo taken while
 * the drain was running — to still find the day it belongs to.
 */
export const RECEIPT_RETENTION_MS = 24 * 3_600_000;

/**
 * Exponential backoff: 1, 2, 4, 8 minutes. A construction site with patchy
 * signal reconnects in bursts, so retrying tightly just burns battery — and the
 * `online` event triggers an immediate drain anyway, which is the path that
 * actually matters.
 */
export function backoffMs(attempts: number): number {
  const minutes = Math.pow(2, Math.max(0, attempts - 1));
  return Math.min(minutes, 16) * 60_000;
}

/**
 * Firebase callable errors carry a `code`. Distinguishing the two families is
 * the difference between a photo that eventually uploads and one that retries
 * forever against a server that will never accept it.
 *
 * Anything unrecognised is treated as RETRYABLE: a transient network blip that
 * we drop would lose a worker's hours, whereas a permanent error that we retry
 * costs a few wasted requests and then hits MAX_ATTEMPTS.
 */
export function isPermanentFailure(err: unknown): boolean {
  const code = extractCode(err);
  if (!code) return false;
  return [
    'functions/invalid-argument',
    'functions/permission-denied',
    'functions/unauthenticated',
    'functions/not-found',
    'functions/already-exists',
    'functions/failed-precondition',
    'functions/out-of-range',
    'storage/unauthorized',
    'storage/object-not-found',
    'storage/invalid-argument',
    'permission-denied',
    'invalid-argument',
    'not-found',
    'failed-precondition',
  ].includes(code);
}

function extractCode(err: unknown): string | undefined {
  if (typeof err !== 'object' || err === null) return undefined;
  const code = (err as { code?: unknown }).code;
  return typeof code === 'string' ? code.toLowerCase() : undefined;
}

function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  return String(err);
}

export interface EnqueueInput {
  type: SyncActionType;
  payload: Record<string, unknown>;
  blob?: Blob;
  blobName?: string;
  blobType?: string;
  dedupeKey?: string;
  /** This action creates a working day, known locally by this name. */
  localRef?: string;
  /** This action needs the timesheet id that `localRef` will produce. */
  awaitsRef?: string;
  afterActionId?: string;
}

/**
 * Append an action. `id` and `createdAt` are injected rather than generated
 * here so the caller controls identity (and tests stay deterministic).
 *
 * When `dedupeKey` matches an action already waiting, the EXISTING one is
 * returned untouched — the worker's first tap is the one that counts, and its
 * original `createdAt` is what the server should see.
 */
export async function enqueue(
  storage: SyncQueueStorage,
  input: EnqueueInput,
  id: string,
  now: Date,
): Promise<QueuedAction> {
  if (input.dedupeKey) {
    const existing = (await storage.list()).find(
      (a) => a.dedupeKey === input.dedupeKey && !a.failedPermanently && !a.completedAt,
    );
    if (existing) return existing;
  }
  const action: QueuedAction = {
    id,
    type: input.type,
    payload: input.payload,
    blob: input.blob,
    blobName: input.blobName,
    blobType: input.blobType,
    dedupeKey: input.dedupeKey,
    ...(input.localRef ? { localRef: input.localRef } : {}),
    ...(input.awaitsRef ? { awaitsRef: input.awaitsRef } : {}),
    ...(input.afterActionId ? { afterActionId: input.afterActionId } : {}),
    createdAt: now.toISOString(),
    attempts: 0,
  };
  await storage.put(action);
  return action;
}

/** A receipt is a finished producer kept only to hold its answer. */
export function isReceipt(a: QueuedAction): boolean {
  return !!a.completedAt;
}

/** Actions still waiting to sync (excludes receipts and permanent failures). */
export function pendingActions(actions: QueuedAction[]): QueuedAction[] {
  return actions.filter((a) => !a.failedPermanently && !isReceipt(a));
}

/** Actions the worker needs to be told about — they will not retry on their own. */
export function failedActions(actions: QueuedAction[]): QueuedAction[] {
  return actions.filter((a) => a.failedPermanently && !isReceipt(a));
}

/**
 * Run every due action, oldest first.
 *
 * ORDER MATTERS and is why this is strictly sequential rather than parallel: a
 * `completeWorkDay` references a timesheet whose CREATION may itself still be an
 * unflushed Firestore write, and two actions on the same sheet must land in the
 * order the worker performed them. The caller is responsible for flushing
 * Firestore's own write queue first (see `services/syncQueue.ts`).
 *
 * A permanent failure is flagged and left in the queue rather than deleted, so
 * the UI can tell the worker their hours did not sync instead of losing them.
 */
export async function drainQueue(
  storage: SyncQueueStorage,
  executors: ExecutorMap,
  now: Date,
): Promise<DrainResult> {
  const result: DrainResult = { succeeded: 0, deferred: 0, failed: 0, skipped: 0, waiting: 0 };
  const all = await storage.list();
  const due = all
    .filter((a) => !a.failedPermanently && !isReceipt(a))
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));

  /**
   * localRef → the timesheet id the server gave it.
   *
   * Seeded from the RECEIPTS already in storage, not just from what happens
   * in this pass: a drain interrupted after a start succeeded must not come
   * back and decide the start had failed.
   */
  const resolved = new Map<string, string>();
  for (const a of all) {
    if (isReceipt(a) && a.localRef && a.resolvedTimesheetId) {
      resolved.set(a.localRef, a.resolvedTimesheetId);
    }
  }
  /** Which refs still have a producer that might yet succeed. */
  const liveRefs = new Set(due.map((a) => a.localRef).filter((r): r is string => !!r));

  const outstanding = new Set(
    all.filter((a) => !isReceipt(a) && !a.failedPermanently).map((a) => a.id),
  );
  for (const action of due) {
    if (action.afterActionId && outstanding.has(action.afterActionId)) {
      result.waiting = (result.waiting ?? 0) + 1;
      continue;
    }
    if (action.nextAttemptAt && action.nextAttemptAt > now.toISOString()) {
      result.skipped += 1;
      continue;
    }

    let toRun = action;
    if (action.awaitsRef) {
      const timesheetId = resolved.get(action.awaitsRef);
      if (timesheetId) {
        // The day exists now. Rewrite the reference and persist it, so a
        // crash between here and the call does not lose the link.
        toRun = { ...action, payload: { ...action.payload, timesheetId }, awaitsRef: undefined };
        await storage.put(toRun);
      } else if (liveRefs.has(action.awaitsRef)) {
        // Its day has not been created yet — this pass or at all. Wait; do
        // NOT run it against a timesheet id that does not exist.
        result.waiting = (result.waiting ?? 0) + 1;
        continue;
      } else {
        // Nothing left that could ever produce it: the start failed for
        // good, or was cleared. The worker's photo and hours still exist
        // here and must be shown to them rather than retried forever
        // against a day that will never be created.
        await storage.put({
          ...action,
          failedPermanently: true,
          lastError: 'DEPENDENCY_FAILED',
          errorCode: 'DEPENDENCY_FAILED',
        });
        outstanding.delete(action.id);
        result.failed += 1;
        continue;
      }
    }

    const executor = executors[toRun.type];
    if (!executor) {
      // An action type this build no longer knows how to run (e.g. a queue
      // written by a newer version). Flag rather than silently retry forever.
      await storage.put({
        ...toRun,
        failedPermanently: true,
        lastError: `Unknown action type: ${toRun.type}`,
        errorCode: 'unknown',
      });
      outstanding.delete(action.id);
      result.failed += 1;
      continue;
    }

    try {
      const outcome = await executor(toRun);
      if (toRun.localRef && outcome?.timesheetId) {
        resolved.set(toRun.localRef, outcome.timesheetId);
        // Durable BEFORE the producer stops existing. If the process dies on
        // the next line, the next pass still knows what the server said.
        await storage.put({
          ...toRun,
          completedAt: now.toISOString(),
          resolvedTimesheetId: outcome.timesheetId,
        });
      } else {
        await storage.remove(toRun.id);
      }
      outstanding.delete(toRun.id);
      if (toRun.localRef) liveRefs.delete(toRun.localRef);
      result.succeeded += 1;
    } catch (err) {
      const attempts = toRun.attempts + 1;
      const permanent = isPermanentFailure(err) || attempts >= MAX_ATTEMPTS;
      await storage.put({
        ...toRun,
        attempts,
        lastError: errorMessage(err),
        errorCode:
          (err as { details?: { reason?: string } })?.details?.reason ??
          (err as { details?: { conflict?: string } })?.details?.conflict ??
          extractCode(err)?.replace(/^functions\//, '') ??
          'unknown',
        failedPermanently: permanent || undefined,
        nextAttemptAt: permanent
          ? undefined
          : new Date(now.getTime() + backoffMs(attempts)).toISOString(),
      });
      // A start that has failed for good can no longer produce its day. Its
      // dependants are dealt with in this same pass, above, rather than
      // being left to retry against an id that will never arrive.
      if (permanent) outstanding.delete(toRun.id);
      if (permanent && toRun.localRef) liveRefs.delete(toRun.localRef);
      if (permanent) result.failed += 1;
      else result.deferred += 1;
    }
  }

  // Retire receipts — but not the moment the last KNOWN dependant is dealt
  // with. A photo taken while the drain was running is enqueued against the
  // ref after its producer has already gone through, so "nothing is waiting
  // right now" is not the same as "nothing will". They are kept for a day,
  // which costs a handful of tiny records and is the difference between a
  // late photo finding its day and a worker being told their start failed.
  const remaining = await storage.list();
  const stillAwaited = new Set(
    remaining.filter((a) => !isReceipt(a) && a.awaitsRef).map((a) => a.awaitsRef),
  );
  for (const a of remaining) {
    if (!isReceipt(a)) continue;
    if (a.localRef && stillAwaited.has(a.localRef)) continue;
    const age = now.getTime() - Date.parse(a.completedAt ?? a.createdAt);
    if (!(age >= 0) || age >= RECEIPT_RETENTION_MS) await storage.remove(a.id);
  }

  return result;
}

/**
 * Clear the permanent-failure flag so the worker can retry by hand (e.g. after
 * an admin fixed the underlying sheet). Attempts reset so backoff starts fresh.
 */
export async function retryFailed(storage: SyncQueueStorage): Promise<number> {
  const all = await storage.list();
  const failed = all.filter((a) => a.failedPermanently && a.errorCode !== 'MEDIA_REMOVED');
  for (const action of failed) {
    await storage.put({
      ...action,
      attempts: 0,
      failedPermanently: undefined,
      nextAttemptAt: undefined,
      lastError: undefined,
      errorCode: undefined,
    });
  }
  return failed.length;
}
